/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { SessionPayload } from '../components/figma/AuthForm';
import { API_BASE_URL } from '../lib/api';

const STORAGE_KEY = 'offibat-auth-session';

type AuthContextValue = {
  session: SessionPayload | null;
  setSession: (session: SessionPayload | null) => void;
  isAuthenticated: boolean;
  refreshSession: () => Promise<void>;
  updateSessionScore: (score: number) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSessionState] = useState<SessionPayload | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as SessionPayload;
        if (parsed?.sessionId && parsed?.user?.fullName) {
          setSessionState(parsed);
        }
      }
    } catch (error) {
      console.error('Unable to restore session', error);
    }
  }, []);

  const setSession = useCallback((value: SessionPayload | null) => {
    if (value) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setSessionState(value);
  }, []);

  const refreshSession = useCallback(async () => {
    if (!session?.sessionId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/session/${session.sessionId}`);
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération de la session');
      }
      const payload = (await response.json()) as SessionPayload;
      setSession(payload);
    } catch (error) {
      console.error('Unable to refresh session', error);
    }
  }, [session?.sessionId, setSession]);

  const updateSessionScore = useCallback(async (score: number) => {
    if (!session?.sessionId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/session/${session.sessionId}/score`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ score }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => undefined);
        const message = body?.message ?? 'Impossible de mettre a jour le score';
        throw new Error(Array.isArray(message) ? message.join(', ') : message);
      }

      const payload = (await response.json()) as SessionPayload;
      setSession(payload);
    } catch (error) {
      console.error('Unable to update session score', error);
      throw error;
    }
  }, [session?.sessionId, setSession]);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    setSession,
    isAuthenticated: Boolean(session),
    refreshSession,
    updateSessionScore,
  }), [session, setSession, refreshSession, updateSessionScore]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
